import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Edit, Trash2, Tag } from "lucide-react";
import { EventCategory } from "@/hooks/useEventManagement";

interface EventCategoryItemProps {
  category: EventCategory;
  index: number;
  onEdit: (categoryId: string) => void;
  onDelete: (categoryId: string) => void;
  onToggleActive: (categoryId: string, currentActive: boolean) => void;
  reordering: boolean;
}

export function EventCategoryItem({
  category,
  index,
  onEdit,
  onDelete,
  onToggleActive,
  reordering,
}: EventCategoryItemProps) {
  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Tag className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-sm sm:text-base truncate">
                {category.display_name}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {category.category_key}
                </Badge>
                <Badge 
                  variant={category.is_active ? "default" : "secondary"}
                  className={`text-xs ${category.is_active ? "bg-green-100 text-green-800 hover:bg-green-100" : ""}`}
                >
                  {category.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(category.id)}
              disabled={reordering}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(category.id)}
              disabled={reordering}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {category.description && (
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground line-clamp-2">
            {category.description}
          </p>
        </CardContent>
      )}
    </Card>
  );
}